import { format, startOfMonth, subMonths } from 'date-fns';

import { User } from '@/store/slices/auth/types';
import { WorkActivityPerformanceResponse } from '@/services/types/work-activity.types';
import { BudgetMember } from '@/services/types/budget-members.types';

export const CURRENT_USER_LINE_COLOR = 'hsl(248, 77%, 65%)';

export const OTHER_MEMBER_LINE_COLORS = ['#E8507E', '#32ADE6', '#FF9500', '#34C759', '#AF52DE'];

export interface MemberHoursDataPoint {
  date: string;
  timestamp: number;
  hours: number;
  monthlyHours?: number;
}

export interface BudgetMemberHoursSeries {
  userId: string;
  name: string;
  photoURL?: string;
  assignedHours: number;
  dataPoints: MemberHoursDataPoint[];
}

export type BudgetMembersChartRow = {
  date: string;
  timestamp: number;
  [memberKey: string]: string | number;
};

const getSeriesKey = (userId: string) => `member_${userId}`;

export const buildBudgetMembersChartRows = (series: BudgetMemberHoursSeries[]): BudgetMembersChartRow[] => {
  const dateMap = new Map<number, BudgetMembersChartRow>();

  series.forEach((member) => {
    member.dataPoints.forEach((point) => {
      const existing = dateMap.get(point.timestamp) ?? {
        date: point.date,
        timestamp: point.timestamp
      };

      existing[getSeriesKey(member.userId)] = point.hours;
      dateMap.set(point.timestamp, existing as BudgetMembersChartRow);
    });
  });

  return Array.from(dateMap.values()).sort((a, b) => a.timestamp - b.timestamp);
};

export const getBudgetMembersChartYMax = (series: BudgetMemberHoursSeries[]): number => {
  const maxAssigned = Math.max(...series.map((member) => member.assignedHours), 0);
  const maxRecorded = Math.max(...series.flatMap((member) => member.dataPoints.map((point) => point.hours)), 0);
  const rawMax = Math.max(maxAssigned, maxRecorded, 100);

  return Math.ceil(rawMax / 100) * 100;
};

export const getMemberLineColor = (member: BudgetMemberHoursSeries, currentUserId: string, index: number): string => {
  if (member.userId === currentUserId) {
    return CURRENT_USER_LINE_COLOR;
  }

  const paletteIndex = index % OTHER_MEMBER_LINE_COLORS.length;
  return OTHER_MEMBER_LINE_COLORS[paletteIndex];
};

const parsePerformanceMonthTimestamp = (monthKey: string): number => {
  const [year, month] = monthKey.split('-').map(Number);
  return Date.UTC(year, month - 1, 1);
};

export const mapWorkActivityPerformanceToChartSeries = (
  performance: WorkActivityPerformanceResponse,
  members: BudgetMember[] = []
): BudgetMemberHoursSeries[] => {
  return performance.series.map((entry) => {
    const matchedMember = members.find(
      (member) => member.user_id === entry.member.user_id || member._id === entry.member.member_id
    );

    return {
      userId: entry.member.user_id,
      name: entry.member.name,
      photoURL: entry.member.profile_pic_url ?? undefined,
      assignedHours: matchedMember?.ore_hours ?? 0,
      dataPoints: entry.points.map((point) => ({
        date: point.label,
        timestamp: parsePerformanceMonthTimestamp(point.month),
        hours: point.cumulative_hours,
        monthlyHours: point.monthly_hours
      }))
    };
  });
};

export const generateMonthlyDatePoints = (monthsBack = 6): MemberHoursDataPoint[] => {
  const today = new Date();
  const points: MemberHoursDataPoint[] = [];

  for (let i = monthsBack; i >= 0; i--) {
    const date = startOfMonth(subMonths(today, i));
    points.push({
      date: format(date, 'dd MMM yyyy'),
      timestamp: date.getTime(),
      hours: 0
    });
  }

  return points;
};

export const getMockBudgetMemberHoursSeries = (currentUser: User | null): BudgetMemberHoursSeries[] => {
  const datePoints = generateMonthlyDatePoints(6);
  const currentUserId = currentUser?.id ?? 'current-user';
  const currentUserName =
    currentUser?.name || `${currentUser?.firstName ?? 'Marco'} ${currentUser?.lastName ?? 'Bertolazzi'}`.trim();

  return [
    {
      userId: currentUserId,
      name: currentUserName,
      photoURL: currentUser?.photoURL,
      assignedHours: 500,
      dataPoints: datePoints.map((point, index) => ({
        ...point,
        hours: [0, 0, 300, 300, 350, 380, 395][index] ?? 395
      }))
    },
    {
      userId: 'member-2',
      name: 'Sarah Johnson',
      assignedHours: 250,
      dataPoints: datePoints.map((point, index) => ({
        ...point,
        hours: [0, 0, 0, 20, 80, 140, 185][index] ?? 185
      }))
    },
    {
      userId: 'member-3',
      name: 'Luca Romano',
      assignedHours: 200,
      dataPoints: datePoints.map((point, index) => ({
        ...point,
        hours: [0, 0, 50, 55, 90, 120, 150][index] ?? 150
      }))
    }
  ];
};

export { getSeriesKey };
